import { useQuery, useMutation } from '@apollo/client'
import { createContext, PropsWithChildren, useContext, useState } from 'react'
import {
  MUTATE_APPLY_DISCOUNT_CART_CONSUMER,
  MUTATE_REMOVE_DISCOUNT_CART_CONSUMER,
  MUTATE_APPLY_REWARD_CART_CONSUMER,
  MUTATE_CREATE_CART_CONSUMER,
  MUTATE_REMOVE_ORDER_ITEMS_CONSUMER,
  MUTATE_UPDATE_CART_CONSUMER,
  MUTATE_UPDATE_FULFILLMENT_CART_CONSUMER,
  MUTATE_UPDATE_ORDER_ITEM_QUANTITY_CONSUMER,
  QUERY_GET_CART_CONSUMER,
  QUERY_VALIDATE_CART_CONSUMER,
  consumerClientContext,
  MUTATE_CREATE_ORDER_ITEM_CONSUMER
} from 'shop/client'
import {
  ConsumerCart,
  ConsumerCartPayload,
  CreateCartResponse,
  CartMutationResponse,
  ConsumerCartUpdateResponse,
  ConsumerCartUpdatePayload,
  ConsumerCartFulfillmentUpdatePayload,
  ConsumerCartFulfillmentUpdateResponse,
  ConsumerCartWarning,
  ConsumerCartError,
  ConsumerCartApplyRewardResponse,
  ConsumerCartApplyDiscountResponse,
  ConsumerCartRemoveDiscountResponse,
  ConsumerCartRemoveOrderItemsResponse,
  ConsumerCartRemoveOrderItemsPayload,
  ConsumerCartValidations,
  ConsumerCartValidationsResponse,
  ConsumerCartResponse,
  ConsumerCartUpdateOrderItemQuantityResponse,
  ConsumerCartUpdateOrderItemQuantityPayload,
  ConsumerCartCreateOrderItemPayload,
  ConsumerCartCreateOrderItemResponse,
  AffectedProduct
} from 'shop/types/cart'
import { useShop } from './useGlobalContext'
import {
  getCurrentCartId,
  saveCartToLocalStorage
} from 'shop/components/Cart/utils'
import { ApiError } from 'shop/types'

interface CartState {
  cart: ConsumerCart | null
  setCart: (cart: ConsumerCart | null) => void
  cartLoading: boolean
  cartError: string | null
  setCartError: (error: string | null) => void
  initConsumerCart: (createCartPayload: {
    variables: ConsumerCartPayload
  }) => Promise<CartMutationResponse | void>
  getConsumerCart: () => Promise<ConsumerCart | null>
  updateConsumerCart: (
    variables: ConsumerCartUpdatePayload,
    options?: { skipLoading: boolean }
  ) => Promise<CartMutationResponse | null>
  updateFulfillmentConsumerCart: (
    variables: ConsumerCartFulfillmentUpdatePayload
  ) => Promise<CartMutationResponse | null>
  applyDiscountConsumerCart: ({
    variables
  }: {
    variables: { discountCode: string }
  }) => Promise<CartMutationResponse | null>
  removeDiscountConsumerCart: () => Promise<ConsumerCart | null>
  createOrderItemConsumerCart: (
    variables: ConsumerCartCreateOrderItemPayload
  ) => Promise<CartMutationResponse | null>
  removeOrderItemsConsumerCart: (
    variables: ConsumerCartRemoveOrderItemsPayload
  ) => Promise<CartMutationResponse | null>
  updateOrderItemQuantityConsumerCart: (
    variables: ConsumerCartUpdateOrderItemQuantityPayload
  ) => Promise<CartMutationResponse | null>
  applyRewardConsumerCart: ({
    variables
  }: {
    variables: { rewardId: string }
  }) => Promise<ConsumerCart | null>
  clearConsumerCart: () => void
  validateCart: (
    skipValidate: boolean
  ) => Promise<ConsumerCartValidations | null>
  warnings: ConsumerCartWarning[]
  errors: ConsumerCartError[]
  affectedProducts: AffectedProduct[]
}

const CartContext = createContext<CartState>({
  cart: null,
  setCart: () => null,
  cartLoading: false,
  cartError: null,
  initConsumerCart: () => new Promise<CartMutationResponse>(() => {}),
  getConsumerCart: () => new Promise<ConsumerCart>(() => {}),
  updateConsumerCart: (
    variables: ConsumerCartUpdatePayload,
    options?: { skipLoading: boolean }
  ) => new Promise<CartMutationResponse>(() => {}),
  updateFulfillmentConsumerCart: (
    variables: ConsumerCartFulfillmentUpdatePayload
  ) => new Promise<CartMutationResponse>(() => {}),
  applyDiscountConsumerCart: () => new Promise<CartMutationResponse>(() => {}),
  removeDiscountConsumerCart: () => new Promise<ConsumerCart>(() => {}),
  createOrderItemConsumerCart: (
    variables: ConsumerCartCreateOrderItemPayload
  ) => new Promise<CartMutationResponse | null>(() => {}),
  removeOrderItemsConsumerCart: (
    variables: ConsumerCartRemoveOrderItemsPayload
  ) => new Promise<CartMutationResponse | null>(() => {}),
  updateOrderItemQuantityConsumerCart: () =>
    new Promise<CartMutationResponse | null>(() => {}),
  applyRewardConsumerCart: () => new Promise<ConsumerCart>(() => {}),
  setCartError: () => null,
  clearConsumerCart: () => null,
  validateCart: (skipValidate: boolean) =>
    new Promise<ConsumerCartValidations | null>(() => {}),
  warnings: [],
  errors: [],
  affectedProducts: []
})

export const ConsumerCartProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const { config } = useShop()
  const { partnerId, domain } = config
  const [cart, setCart] = useState<ConsumerCart | null>(null)
  const [cartLoading, setCartLoading] = useState<boolean>(false)
  const [cartError, setCartError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<ConsumerCartWarning[]>([])
  const [errors, setErrors] = useState<ConsumerCartError[]>([])
  const [affectedProducts, setAffectedProducts] = useState<AffectedProduct[]>(
    []
  )

  const [createCart] = useMutation<CreateCartResponse, ConsumerCartPayload>(
    MUTATE_CREATE_CART_CONSUMER,
    consumerClientContext
  )
  const { refetch: refetchConsumerCart } = useQuery<ConsumerCartResponse>(
    QUERY_GET_CART_CONSUMER,
    {
      ...consumerClientContext,
      fetchPolicy: 'network-only',
      skip: true
    }
  )
  const { refetch: refetchValidateCart } =
    useQuery<ConsumerCartValidationsResponse>(QUERY_VALIDATE_CART_CONSUMER, {
      ...consumerClientContext,
      skip: true
    })
  const [updateCart] = useMutation<
    ConsumerCartUpdateResponse,
    ConsumerCartUpdatePayload
  >(MUTATE_UPDATE_CART_CONSUMER, consumerClientContext)

  const [updateCartFulfillment] = useMutation<
    ConsumerCartFulfillmentUpdateResponse,
    ConsumerCartFulfillmentUpdatePayload
  >(MUTATE_UPDATE_FULFILLMENT_CART_CONSUMER, consumerClientContext)

  const [applyDiscount] = useMutation<ConsumerCartApplyDiscountResponse>(
    MUTATE_APPLY_DISCOUNT_CART_CONSUMER,
    consumerClientContext
  )
  const [removeDiscount] = useMutation<ConsumerCartRemoveDiscountResponse>(
    MUTATE_REMOVE_DISCOUNT_CART_CONSUMER,
    consumerClientContext
  )

  const [applyReward] = useMutation<ConsumerCartApplyRewardResponse>(
    MUTATE_APPLY_REWARD_CART_CONSUMER,
    consumerClientContext
  )

  const [createOrderItem] = useMutation<
    ConsumerCartCreateOrderItemResponse,
    ConsumerCartCreateOrderItemPayload
  >(MUTATE_CREATE_ORDER_ITEM_CONSUMER, consumerClientContext)

  const [removeOrderItems] = useMutation<
    ConsumerCartRemoveOrderItemsResponse,
    ConsumerCartRemoveOrderItemsPayload
  >(MUTATE_REMOVE_ORDER_ITEMS_CONSUMER, consumerClientContext)

  const [updateOrderItemQuantity] = useMutation<
    ConsumerCartUpdateOrderItemQuantityResponse,
    ConsumerCartUpdateOrderItemQuantityPayload
  >(MUTATE_UPDATE_ORDER_ITEM_QUANTITY_CONSUMER, consumerClientContext)

  const createConsumerCart = async (createCartPayload: {
    variables: ConsumerCartPayload
  }): Promise<CreateCartResponse> => {
    const { data } = await createCart(createCartPayload)
    return data as CreateCartResponse
  }

  const initConsumerCart = (createCartPayload: {
    variables: ConsumerCartPayload
  }): Promise<CartMutationResponse | void> => {
    setCartLoading(true)
    return createConsumerCart(createCartPayload)
      .then(async ({ createCart }: CreateCartResponse) => {
        if (!createCart) {
          return
        }

        const { cart, errors, warnings } = createCart

        handleCartResponse(cart, errors, warnings)
        handleAffectedProducts(cart?.summary?.discount?.affectedProducts)
        // cart creation success, clear any previous hard errors.
        setCartError(null)

        const { id: cartId } = cart

        saveCartToLocalStorage(cartId, { slug: domain, id: partnerId })

        return createCart
      })
      .catch((createCartError: ApiError) => {
        setCartError('cart_creation_error')
      })
      .finally(() => {
        setCartLoading(false)
      })
  }

  const getConsumerCart = (): Promise<ConsumerCart | null> => {
    setCartLoading(true)
    const cartId = cart?.id || getCurrentCartId(config.domain)
    if (!cartId) {
      setCartLoading(false)
      return Promise.resolve(null)
    }

    return refetchConsumerCart()
      .then(({ data }) => {
        if (data?.cart) {
          setCart(data.cart)
          setCartLoading(false)
          return data.cart
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        setCartError(error)
        setCartLoading(false)
        throw error
      })
  }

  const updateConsumerCart = (
    variables: ConsumerCartUpdatePayload,
    options: { skipLoading: boolean } = { skipLoading: false }
  ): Promise<CartMutationResponse | null> => {
    if (!options.skipLoading) {
      setCartLoading(true)
    }
    return updateCart({ variables })
      .then(({ data }) => {
        const { updateCart } = data || {}
        if (updateCart) {
          const { cart, errors, warnings } = updateCart
          handleCartResponse(cart, warnings, errors)
          setCartLoading(false)
          return updateCart
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        setCartLoading(false)
        throw error
      })
  }

  const updateFulfillmentConsumerCart = (
    variables: ConsumerCartFulfillmentUpdatePayload
  ): Promise<CartMutationResponse | null> => {
    setCartLoading(true)
    return updateCartFulfillment({ variables })
      .then(({ data }) => {
        const { updateCartFulfillment } = data || {}
        if (updateCartFulfillment) {
          const { errors, warnings, cart } = updateCartFulfillment
          handleCartResponse(cart, warnings, errors)
          setCartLoading(false)
          return updateCartFulfillment
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        throw error
      })
      .finally(() => {
        setCartLoading(false)
      })
  }

  /** Exposes validateCart query & handles response */
  const validateCart = (skipValidate: boolean = false) => {
    if (skipValidate) return Promise.resolve(null)
    return refetchValidateCart()
      .then(({ data }: { data: ConsumerCartValidationsResponse }) => {
        if (data) {
          const { validateCart } = data
          const { warnings, errors } = validateCart || {}
          handleValidations(warnings, errors)
          return validateCart
        }
        return null
      })
      .catch((error) => {
        throw error
      })
  }

  /** Sets cart in state & calls cart response handling functions */
  const handleCartResponse = (
    cart: ConsumerCart,
    warnings: ConsumerCartWarning[],
    errors: ConsumerCartError[]
  ) => {
    // Only set cart if it is non null (otherwise keep existing cart to avoid removing cart details on error */
    !!cart && setCart(cart)
    handleValidations(warnings, errors)
    handleAffectedProducts(cart?.summary?.discount?.affectedProducts)
  }

  /** Sets and removes errors/warnings in case of them being cleared */
  const handleValidations = (
    warnings: ConsumerCartWarning[],
    errors: ConsumerCartError[]
  ) => {
    setWarnings(warnings.length ? warnings : [])
    setErrors(errors.length ? errors : [])
  }

  /** Sets & removes affected products when discount is applied */
  const handleAffectedProducts = (
    affProducts: AffectedProduct[] | undefined
  ) => {
    // If affectedProducts is falsy, do nothing
    if (!affProducts) return
    // Otherwise, set affected products
    setAffectedProducts(affProducts)
  }

  const applyDiscountConsumerCart = ({
    variables
  }: {
    variables: { discountCode: string }
  }): Promise<CartMutationResponse | null> => {
    setCartLoading(true)
    return applyDiscount({ variables })
      .then(({ data }) => {
        const { applyDiscount } = data || {}
        if (applyDiscount) {
          const { cart, warnings, errors } = applyDiscount
          handleCartResponse(cart, warnings, errors)
          setCartLoading(false)
          return applyDiscount
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        setCartLoading(false)
        throw error
      })
  }

  const removeDiscountConsumerCart = (): Promise<ConsumerCart | null> => {
    setCartLoading(true)
    return removeDiscount()
      .then(({ data }) => {
        const { removeDiscount } = data || {}
        if (removeDiscount) {
          const { cart, warnings, errors } = removeDiscount
          handleCartResponse(cart, warnings, errors)
          setCartLoading(false)
          return cart
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        setCartLoading(false)
        throw error
      })
  }

  const applyRewardConsumerCart = ({
    variables
  }: {
    variables: { rewardId: string }
  }): Promise<ConsumerCart | null> => {
    setCartLoading(true)
    return applyReward({ variables })
      .then(({ data }) => {
        const { applyReward } = data || {}
        if (applyReward) {
          const { cart, warnings, errors } = applyReward
          handleCartResponse(cart, warnings, errors)
          setCartLoading(false)
          return cart
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        setCartLoading(false)
        throw error
      })
  }

  const clearConsumerCart = () => {
    setCart(null)
    setCartError(null)
    setAffectedProducts([])
  }

  const removeOrderItemsConsumerCart = (
    variables: ConsumerCartRemoveOrderItemsPayload
  ): Promise<CartMutationResponse | null> => {
    setCartLoading(true)
    return removeOrderItems({ variables })
      .then(({ data }) => {
        const { removeOrderItems } = data || {}
        if (removeOrderItems) {
          const { cart, warnings, errors } = removeOrderItems
          handleCartResponse(cart, warnings, errors)
          setCartLoading(false)
          return removeOrderItems
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        setCartLoading(false)
        throw error
      })
  }

  const updateOrderItemQuantityConsumerCart = (
    variables: ConsumerCartUpdateOrderItemQuantityPayload
  ): Promise<CartMutationResponse | null> => {
    setCartLoading(true)
    return updateOrderItemQuantity({ variables })
      .then(({ data }) => {
        const { updateOrderItemQuantity } = data || {}
        if (updateOrderItemQuantity) {
          const { cart, warnings, errors } = updateOrderItemQuantity
          handleCartResponse(cart, warnings, errors)
          setCartLoading(false)
          return updateOrderItemQuantity
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        setCartLoading(false)
        throw error
      })
  }

  const createOrderItemConsumerCart = (
    variables: ConsumerCartCreateOrderItemPayload
  ): Promise<CartMutationResponse | null> => {
    setCartLoading(true)
    return createOrderItem({ variables })
      .then(({ data }) => {
        const { createOrderItem } = data || {}
        if (createOrderItem) {
          const { cart, warnings, errors } = createOrderItem
          handleCartResponse(cart, warnings, errors)
          setCartLoading(false)
          return createOrderItem
        }
        setCartLoading(false)
        return null
      })
      .catch((error) => {
        setCartLoading(false)
        throw error
      })
  }

  return (
    <CartContext.Provider
      value={{
        applyDiscountConsumerCart,
        applyRewardConsumerCart,
        cart,
        cartError,
        cartLoading,
        clearConsumerCart,
        errors,
        getConsumerCart,
        initConsumerCart,
        removeDiscountConsumerCart,
        removeOrderItemsConsumerCart,
        setCart,
        setCartError,
        updateConsumerCart,
        updateFulfillmentConsumerCart,
        createOrderItemConsumerCart,
        updateOrderItemQuantityConsumerCart,
        validateCart,
        warnings,
        affectedProducts
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useConsumerCart = () => useContext(CartContext)
